import GeoJSON from 'ol/format/GeoJSON';
import GroupLayer from '@planet/maps/layer/Group';
import React, {useMemo} from 'react';
import TileLayer from '@planet/maps/layer/Tile';
import XYZSource from '@planet/maps/source/XYZ';
import {array, bool, func, object, string} from 'prop-types';
import {getVectorContext} from 'ol/render.js';

import CompareRasters from './compare-rasters.jsx';
import {CLIP_STYLE} from '@explorer/util/map-edit-styles';
import {COMPARE_METHODS} from '@explorer/constants';

const CompareTileLayers = ({
  compareTileConfigs = [],
  clipGeometry = null,
  visible,
  onTileLoad,
  values,
  compareMethod,
}) => {
  // useMemo is used instead of useCallback
  //  so that geometry can be used as a closure in the function
  //  that is returned. The object is returned for some
  //  future-proofing in case we need a onPrerender or
  //  the geometry to be broken out.
  const {onPostrender, onPrerender} = useMemo(() => {
    const geometry = clipGeometry
      ? new GeoJSON().readGeometry(clipGeometry)
      : null;

    return {
      onPostrender: () => event => {
        if (geometry) {
          const vectorContext = getVectorContext(event);
          event.context.globalCompositeOperation = 'destination-in';
          vectorContext.setStyle(CLIP_STYLE);
          vectorContext.drawGeometry(geometry);
          event.context.globalCompositeOperation = 'source-over';
        }

        event.context.restore();
      },
      onPrerender: index => event => {
        if (compareMethod === COMPARE_METHODS.SLIDER) {
          const ctx = event.context;
          const width = ctx.canvas.width * values[COMPARE_METHODS.SLIDER];
          const start = index === 1 ? width : 0;
          const end = index === 1 ? ctx.canvas.width - width : width;

          ctx.save();
          ctx.beginPath();
          ctx.rect(start, 0, end, ctx.canvas.height);
          ctx.clip();
        }
      },
    };
  }, [clipGeometry, compareMethod, values]);

  const sourceOptions = useMemo(() => {
    const options = {
      crossOrigin: 'anonymous',
    };

    // this prevents needing to use the fetch loader
    //   when `onTileLoad` is undefined.
    if (onTileLoad) {
      options.tileLoadFunction = (imageTile, src) => {
        return fetch(src)
          .then(res => {
            onTileLoad(res);
            return res.blob();
          })
          .then(blob => {
            const reader = new FileReader();
            reader.onload = evt => {
              imageTile.getImage().src = evt.target.result;
            };
            reader.readAsDataURL(blob);
          })
          .catch(() => {});
      };
    }

    return options;
  }, [onTileLoad]);

  return (
    <GroupLayer visible={visible}>
      {compareTileConfigs.map((compareGroupConfig, idx) => {
        const opacity =
          idx === 1 && compareMethod === COMPARE_METHODS.FADE
            ? values[COMPARE_METHODS.FADE]
            : 1;
        return (
          // eslint-disable-next-line react/no-array-index-key
          <GroupLayer key={`compareLayer${idx}`} opacity={opacity}>
            {compareGroupConfig.rasterConfigs.map(config => (
              <TileLayer
                key={`${config.id}_${config.maxZoom}`}
                options={{
                  extent: config.extent,
                }}
                onPostrender={onPostrender(idx)}
                onPrerender={onPrerender(idx)}
              >
                <XYZSource
                  url={config.url}
                  options={{
                    maxZoom: Math.ceil(config.maxZoom),
                    ...sourceOptions,
                  }}
                />
              </TileLayer>
            ))}
          </GroupLayer>
        );
      })}
      {compareTileConfigs.length === 2 && (
        <CompareRasters
          visible={compareMethod === COMPARE_METHODS.DIFF}
          onPostrender={onPostrender()}
          onPrerender={onPrerender()}
          urls={compareTileConfigs
            .flatMap(group => group.rasterConfigs)
            .map(config => config.url)}
          rangeValues={values[COMPARE_METHODS.DIFF]}
        />
      )}
    </GroupLayer>
  );
};

CompareTileLayers.propTypes = {
  compareTileConfigs: array,
  clipGeometry: object,
  visible: bool,
  onTileLoad: func,
  compareMethod: string,
  values: object,
};

export default CompareTileLayers;
